import React, { useEffect, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import GoogleSignInButton from '../GoogleSignInButton';
import AppleSignInButton from '../AppleSignInButton';
import squareService from '../../services/squareService';
import { DEFAULT_CITY, getCity } from '../../services/userLocation';
import useMediaQuery from '../../utils/useMediaQuery';
import stanchions from '../../assets/img/stanchions-disco-sm.png';
import { SpinningO } from '../portalkit';
import { normalizeInviteCode, readInvite } from '../../utils/inviteRef';

/**
 * ENTER — `/register`, sign-up v3 screen 01 (design_handoff_signup_v3, Oct 22 2026).
 *
 * The register landing: the town pill and the portal up top, the stanchions, FIND YOUR PEOPLE.,
 * "{n} PEOPLE IN {TOWN}", then one card — CONTINUE WITH GOOGLE (Apple above it where Apple
 * sign-in exists), USE EMAIL INSTEAD (the email form at `/register/email`), ALREADY HERE? LOG IN.
 * At 900px and up the headline block and the auth card sit side by side.
 * The town is the location store's (never guessed; `DEFAULT_CITY` until picked) and the count
 * is `GET api/squares/stats`. A brand-new account lands in `/onboard` (`isNewAccount` on the
 * auth response), carrying `?redirect=` / the invite's page as `next`.
 */
export default function EnterScreen() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const phone = useMediaQuery('(max-width: 639px)');
  const desk = useMediaQuery('(min-width: 900px)');
  const city = getCity() || DEFAULT_CITY;
  const [stats, setStats] = useState(null);

  const redirectParam = searchParams.get('redirect') || location.state?.from?.pathname || '';
  const refParam = normalizeInviteCode(searchParams.get('ref'));
  const invitePath = refParam ? (readInvite()?.path || '') : '';
  const from = redirectParam || invitePath || '/square';
  const withParams = (path) => {
    const q = new URLSearchParams();
    if (redirectParam) q.set('redirect', redirectParam);
    if (refParam) q.set('ref', refParam);
    const qs = q.toString();
    return `${path}${qs ? `?${qs}` : ''}`;
  };

  useEffect(() => {
    let on = true;
    squareService.getStats(city).then((r) => { if (on && r.success && r.data) setStats(r.data); });
    return () => { on = false; };
  }, [city]);

  const cityUpper = city.toUpperCase();
  const count = stats?.peopleHere ?? null;

  return (
    <div className="pk" data-testid="enter-screen" style={{ position: 'relative', minHeight: '100dvh', display: 'flex', flexDirection: 'column', background: '#000', color: '#fff', overflowX: 'clip' }}>
      <div aria-hidden="true" style={{ position: 'absolute', left: '50%', top: 0, width: 900, height: 520, transform: 'translateX(-50%)', background: 'radial-gradient(closest-side, rgba(253,25,159,.14), rgba(18,123,253,.08) 55%, transparent)', pointerEvents: 'none' }} />

      <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: phone ? 'calc(env(safe-area-inset-top, 0px) + 18px) 20px 18px' : 'calc(env(safe-area-inset-top, 0px) + 28px) 32px 28px' }}>
        <span data-testid="enter-city" style={{ display: 'flex', alignItems: 'center', gap: 8, height: 36, padding: '0 14px', borderRadius: 999, background: '#161616', fontSize: 10, fontWeight: 800, letterSpacing: '.16em' }}>
          <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: '50%', background: 'linear-gradient(90deg,#FD199F,#127BFD,#22ECFB)' }} />
          {cityUpper}
        </span>
        <SpinningO size={32} />
      </div>

      <div style={{ position: 'relative', flex: 1, display: 'flex', flexDirection: desk ? 'row' : 'column', alignItems: 'center', justifyContent: 'center', gap: desk ? 96 : 34, padding: phone ? '20px 20px calc(env(safe-area-inset-bottom, 0px) + 40px)' : '20px 24px 64px', boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: desk ? 'flex-start' : 'center', gap: 14, textAlign: desk ? 'left' : 'center' }}>
          <img src={stanchions} alt="" style={{ width: desk ? 200 : 150, display: 'block' }} />
          <h1 style={{ margin: 0, fontSize: desk ? 64 : 34, fontWeight: 800, letterSpacing: '.06em', lineHeight: 1.02, textTransform: 'uppercase' }}>FIND YOUR <br />PEOPLE.</h1>
          <span data-testid="enter-count" style={{ fontSize: 12, fontWeight: 600, letterSpacing: '.18em', color: '#8A8A8A', minHeight: 18 }}>
            {count == null ? '' : count > 0 ? `${count} ${count === 1 ? 'PERSON' : 'PEOPLE'} IN ${cityUpper}` : `BE THE FIRST IN ${cityUpper}`}
          </span>
        </div>

        <div style={{ width: '100%', maxWidth: 440, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 22, boxSizing: 'border-box', borderRadius: 28, background: '#111', boxShadow: 'inset 0 0 0 1px #1F1F1F' }}>
          <AppleSignInButton variant="enter" redirect={from} label="Continue with Apple" />
          <GoogleSignInButton variant="enter" redirect={from} label="Continue with Google" />
          <Link to={withParams('/register/email')} data-testid="use-email" className="pk-btn" style={{ height: 52, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 999, background: '#000', boxShadow: 'inset 0 0 0 1px #2A2A2A', color: '#fff', fontSize: 11, fontWeight: 800, letterSpacing: '.16em', textDecoration: 'none', transition: 'box-shadow .2s' }} onMouseEnter={(e) => { e.currentTarget.style.boxShadow = 'inset 0 0 0 1px #fff'; }} onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'inset 0 0 0 1px #2A2A2A'; }}>USE EMAIL INSTEAD</Link>
          <Link to={withParams('/login')} data-testid="enter-login" style={{ alignSelf: 'center', height: 36, display: 'flex', alignItems: 'center', fontSize: 10, fontWeight: 800, letterSpacing: '.16em', color: '#8A8A8A', textDecoration: 'none' }}>ALREADY HERE? LOG IN</Link>
        </div>

        <span style={{ textAlign: 'center', fontSize: 11, lineHeight: 1.6, color: '#6B6B6B' }}>
          By continuing you agree to the <Link to="/terms" style={{ color: '#8A8A8A' }}>Terms</Link> and the <Link to="/privacy" style={{ color: '#8A8A8A' }}>Privacy Policy</Link>.
        </span>
        </div>
      </div>
    </div>
  );
}
